import { useState, useRef, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Shield,
  AlertTriangle,
  FileSearch,
  Lock,
  CheckCircle,
  Settings,
  Printer,
  TrendingUp,
  Target,
  Wrench,
  Scale,
  AlertCircle,
  ArrowLeft,
  XCircle,
  Clock,
  RotateCcw,
  Download,
  ChevronDown,
} from 'lucide-react';
import { useReportData } from '@/hooks/useReportData';
import { useActiveScans } from '@/hooks/useActiveScans';
import { ScanModal } from '@/components/dashboard';
import { Button as UIButton } from '@/components/ui/Button';
import { PageSpinner } from '@/components/ui/PageLayout';
import ReportHeader from '@/components/report/ReportHeader';
import ScoreCard from '@/components/report/ScoreCard';
import ScoreBreakdown from '@/components/report/ScoreBreakdown';
import VulnerabilitySummary from '@/components/report/VulnerabilitySummary';
import KeyFinding from '@/components/report/KeyFinding';
import CreatorVerification from '@/components/report/CreatorVerification';
import VulnerabilityGroup, { groupVulnerabilities } from '@/components/report/VulnerabilityGroup';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/Pagination';
import PermissionScope from '@/components/report/PermissionScope';
import MCPToolsSummary from '@/components/report/MCPToolsSummary';
import RecommendationCard from '@/components/report/RecommendationCard';
import CollapsibleSection from '@/components/report/CollapsibleSection';
import TableOfContents from '@/components/report/TableOfContents';
import ReportFooter from '@/components/report/ReportFooter';
import PluginProfile from '@/components/report/PluginProfile';
import ReportSettings, { SectionVisibility } from '@/components/report/ReportSettings';
import { ReportSubHeader } from '@/components/report/ReportSubHeader';
import { ToolDetail } from '@/components/report/ToolDetailDialog';
import ThreatModeling from '@/components/report/ThreatModeling';
import PrioritizedRemediation from '@/components/report/PrioritizedRemediation';
import RiskAcceptanceMatrix from '@/components/report/RiskAcceptanceMatrix';
import RFIASection from '@/components/report/RFIASection';
import VibeguardSection from '@/components/report/VibeguardSection';
import type { VGEFindingData } from '@/utils/scanToInsights';
import RiskHeatmap from '@/components/report/RiskHeatmap';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { sampleReport } from '@/data/sampleReportData';
import { getStoredToken } from '@/lib/api';
import { getApiUrl } from '@/hooks/useReportData';
import type { ThreatModeling as ThreatModelingData, RFIAFindingData, RFIAAggregateData } from '@/utils/scanToInsights';
import { buildHeatmapFromScores } from '@/utils/scanToInsights';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * Extended report view type — adds optional API fields that exist on
 * dynamic scan data but aren't present on the static sampleReport.
 * Eliminates `as any` casts throughout the page component.
 */
type ReportViewData = typeof sampleReport & {
  branch?: string;
  githubTokenPresent?: boolean;
  prioritizedRemediation?: Array<{ id: string; title: string; description: string; riskLevel: string; priority: string; effort: string; estimatedTime: string; category?: string }>;
  riskAcceptance?: Array<{ id: string; title: string; description: string; riskLevel: string; justification: string; owner: string; reviewDate: string; status?: string }>;
  scores: typeof sampleReport['scores'] & {
    crs?: { score: number; level: 'critical' | 'high' | 'medium' | 'low' };
  };

  threatModeling: ThreatModelingData & {
    threatActors: typeof sampleReport['threatModeling']['threatActors'];
    attackVectors: typeof sampleReport['threatModeling']['attackVectors'];
  };
  rfiaFindings?: RFIAFindingData[];
  rfiaAggregate?: RFIAAggregateData;
  vibeguardFindings?: VGEFindingData[];
};
/** Shared outer chrome for narrow single-card views (error, no-data, etc.) */
/**
 * Normalize a score value from either API shape into the object form the UI expects.
 *   - Flat number:  7.2               → { score: 7.2, level: 'high' }
 *   - Object form:  { score: 7.2, level: 'high' } → passthrough
 * Defined at module level so it is not recreated on every render.
 */
function normalizeScore(v: unknown, fallback: number = 0): { score: number; level: string } {
  if (v && typeof v === 'object' && 'score' in v) {
    return { score: (v as any).score ?? fallback, level: (v as any).level ?? 'low' };
  }
  if (typeof v === 'number') {
    const score = v;
    const level = score >= 8 ? 'critical' : score >= 6 ? 'high' : score >= 4 ? 'medium' : 'low';
    return { score, level };
  }
  return { score: fallback, level: 'low' };
}

function ReportPageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-[rgb(5,5,5)]">
      <div className="h-1 w-full bg-gradient-to-r from-red-600 via-yellow-500 to-green-500" />
      <div className="container mx-auto px-6 py-16 max-w-2xl">{children}</div>
    </div>
  );
}

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

// Tool details for clickable tools
const toolDetails: Record<string, ToolDetail> = {
  Task: {
    name: 'Task',
    description: 'Creates and manages sub-tasks with delegated permissions.',
    riskLevel: 'medium',
    riskReason:
      'Can delegate work to sub-agents, increasing attack surface. Bounded by parent permissions but adds complexity.',
    capabilities: ['Create sub-tasks', 'Manage task dependencies', 'Coordinate workflows'],
  },
  Agent: {
    name: 'Agent',
    description: 'Spawns autonomous sub-agents with delegated permissions.',
    riskLevel: 'medium',
    riskReason:
      'Sub-agents operate with delegated autonomy. Could amplify vulnerabilities if not properly constrained.',
    capabilities: ['Spawn sub-agents', 'Delegate permissions', 'Parallel execution'],
  },
  TodoRead: {
    name: 'TodoRead',
    description: 'Reads TODO items and task lists from the codebase.',
    riskLevel: 'low',
    riskReason: 'Read-only access to task metadata. Minimal security impact.',
    capabilities: ['Read TODO comments', 'Parse task files', 'Track work items'],
  },
};

// TOC items with their corresponding visibility keys
const tocItemsConfig: Array<{
  id: string;
  label: string;
  visibilityKey: keyof SectionVisibility | 'heatmaps';
}> = [
  { id: 'key-findings', label: 'Key Findings', visibilityKey: 'keyFindings' },
  { id: 'plugin-profile', label: 'Plugin Profile', visibilityKey: 'pluginProfile' },
  {
    id: 'creator-verification',
    label: 'VendorGuard (Creator Verification)',
    visibilityKey: 'creatorVerification',
  },
  { id: 'scoring-breakdown', label: 'Scoring Breakdown', visibilityKey: 'scoringBreakdown' },
  { id: 'risk-heatmaps', label: 'Risk Heatmaps', visibilityKey: 'heatmaps' }, // Unified — dropdown selects scoring model
  {
    id: 'rfia',
    label: 'Rules File Integrity',
    visibilityKey: 'rfiaSection',
  },
  {
    id: 'vibeguard',
    label: 'VibeGuard Engine',
    visibilityKey: 'vibeguardSection',
  },
  {
    id: 'vulnerability-assessment',
    label: 'Vulnerability Assessment',
    visibilityKey: 'vulnerabilityAssessment',
  },
  { id: 'threat-modeling', label: 'Threat Modeling', visibilityKey: 'threatModeling' },
  { id: 'permission-scope', label: 'Permission Scope Analysis', visibilityKey: 'permissionScope' },
  {
    id: 'prioritized-remediation',
    label: 'Prioritized Remediation',
    visibilityKey: 'prioritizedRemediation',
  },
  { id: 'risk-acceptance', label: 'Risk Acceptance Matrix', visibilityKey: 'riskAcceptanceMatrix' },
  { id: 'recommendation', label: 'Recommendation', visibilityKey: 'recommendation' },
];

// Default sample data used as fallback (defined inline above as sampleReport)
// When dynamic data is loaded via useReportData hook, it will be merged with this
// Heatmap generation uses shared buildHeatmapFromScores from scanToInsights

/** Extract githubTokenPresent from vendorGuard data (snake_case or camelCase). */
function extractGithubTokenPresent(creator: Record<string, unknown>): boolean | undefined {
  const vg = creator?.vendorGuard;
  if (vg && typeof vg === 'object') {
    const v = vg as Record<string, unknown>;
    if (typeof v.github_token_present === 'boolean') return v.github_token_present;
    if (typeof v.githubTokenPresent === 'boolean') return v.githubTokenPresent;
  }
  return undefined;
}

const Index = () => {
  useDocumentTitle('Security Report');
  const navigate = useNavigate();
  const [scanIdCopied, copyScanId] = useCopyToClipboard();
  const [retryModalOpen, setRetryModalOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const { removeScan } = useActiveScans();

  // Load report data dynamically (from API, URL params, window injection, or fallback to sample)
  const {
    data: reportDataResult,
    loading,
    error,
    isDynamic,
    scanId,
    scanStatus,
    failureReason,
    failureTarget,
    failureDate,
  } = useReportData();

  // Merge loaded data with sample defaults
  const reportData = useMemo((): ReportViewData => {
    if (!reportDataResult?.report) return sampleReport as ReportViewData;

    // When we have real data, use it directly WITHOUT merging sample data
    // This prevents sample remediationItems, heatmapData, riskAcceptanceItems from bleeding through
    // InsightsReport → ReportViewData: runtime data includes alias fields
    // (remediationItems, heatmapData) that aren't on the canonical interface
    const realData = reportDataResult.report as unknown as ReportViewData;

    // Ensure all required fields exist with proper defaults (not sample data)
    return {
      reportId: realData.reportId || '',
      reportDate: realData.reportDate || '',
      pluginName: realData.pluginName || '',
      branch: realData.branch,
      classification: realData.classification || 'Confidential',
      generatedAt: realData.generatedAt || new Date(),
      scores: (() => {
        // Normalize scores: some API responses return flat numbers ({ aivss: 7.2 })
        // while the UI expects objects ({ aivss: { score: 7.2, level: 'high' } }).
        // normalizeScore coerces either shape into the expected object form.
        // normalizeScore is defined at module level (not inline) — see above;
        const s = realData.scores as any || {};
        return {
          aivss: normalizeScore(s.aivss),
          cvss: normalizeScore(s.cvss),
          aars: normalizeScore(s.aars),
          creatorTrust: normalizeScore(s.creatorTrust),
          overall: typeof s.overall === 'number' ? s.overall : 0,
          ...(s.crs != null ? { crs: normalizeScore(s.crs) } : {}),
        };
      })(),
      scoreBreakdowns: realData.scoreBreakdowns || { aivss: [], cvss: [], aars: [] },
      mcpTools: realData.mcpTools || {
        totalTools: 0,
        highRiskTools: 0,
        mediumRiskTools: 0,
        lowRiskTools: 0,
        toolsList: [],
        toolsInfo: {},
      },
      vulnerabilities: realData.vulnerabilities || {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        info: 0,
      },
      keyFindings: realData.keyFindings || [],
      creator: realData.creator || {
        name: 'Unknown',
        organization: 'Unknown',
        verified: false,
        country: 'Unknown',
        organizationType: 'Unknown',
        trustScore: 0,
      },
      plugin: realData.plugin || {
        name: '',
        description: '',
        version: '',
        location: '',
        category: '',
        license: 'Unknown',
      },
      vulnerabilityDetails: realData.vulnerabilityDetails || [],
      permissions: realData.permissions || [],
      recommendation: realData.recommendation || {
        type: 'conditional',
        summary: '',
        conditions: [],
      },
      threatModeling: realData.threatModeling || { threatActors: [], attackVectors: [] },
      // CRITICAL: These must come from real data or be empty/generated - NEVER from sample
      remediationItems: realData.remediationItems || realData.prioritizedRemediation || [],
      riskAcceptanceItems: realData.riskAcceptanceItems || realData.riskAcceptance || [],
      heatmapData:
        realData.heatmapData ||
        buildHeatmapFromScores(realData.scoreBreakdowns, realData.vulnerabilityDetails),
      aiBom: realData.aiBom || null,
      githubTokenPresent:
        realData.githubTokenPresent ??
        extractGithubTokenPresent(realData.creator) ??
        undefined,
      // RFIA passthrough
      rfiaFindings: realData.rfiaFindings,
      rfiaAggregate: realData.rfiaAggregate,
      // VGE passthrough
      vibeguardFindings: realData.vibeguardFindings,
    } as ReportViewData;
  }, [reportDataResult]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const toolDetailsData = useMemo(() => {
    // Start with hardcoded details and any server-provided details
    const merged: Record<string, ToolDetail> = {
      ...toolDetails,
      ...(reportDataResult?.toolDetails || {}),
    };

    // Auto-generate ToolDetail entries from permissions data so every tool
    // in the Permission Scope table is clickable — not just the hardcoded ones.
    const riskRank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
    const perms = reportData?.permissions;
    if (perms && Array.isArray(perms)) {
      for (const perm of perms) {
        for (const tool of perm.tools || []) {
          if (!merged[tool]) {
            merged[tool] = {
              name: tool,
              description: `This tool provides ${perm.capability.toLowerCase()} functionality.`,
              riskLevel: perm.riskLevel as ToolDetail['riskLevel'],
              riskReason: perm.riskJustification || `Classified as ${perm.riskLevel} risk.`,
              capabilities: [perm.capability],
            };
          } else {
            // Tool appears in multiple permission groups — aggregate capabilities
            const existing = merged[tool];
            if (!existing.capabilities.includes(perm.capability)) {
              existing.capabilities.push(perm.capability);
            }
            // Escalate to the highest risk level seen
            if ((riskRank[perm.riskLevel] || 0) > (riskRank[existing.riskLevel] || 0)) {
              existing.riskLevel = perm.riskLevel as ToolDetail['riskLevel'];
              existing.riskReason = perm.riskJustification || existing.riskReason;
            }
          }
        }
      }
    }

    return merged;
  }, [reportDataResult, reportData?.permissions]);

  const reportRef = useRef<HTMLDivElement>(null);

  /** All possible heatmap options, filtered to those with visibility + data in render */
  const heatmapOptions = useMemo(
    () => [
      {
        value: 'aivss',
        label: 'AIVSS — AI Vulnerability Severity',
        title: 'AIVSS Risk Heatmap',
        subtitle: 'AI Vulnerability Severity Score breakdown',
        visKey: 'heatmapAivss' as const,
        getData: (d: typeof reportData) => d.heatmapData.aivss,
      },
      {
        value: 'cvss',
        label: 'CVSS — Common Vulnerability Scoring',
        title: 'CVSS Risk Heatmap',
        subtitle: 'Common Vulnerability Scoring System breakdown',
        visKey: 'heatmapCvss' as const,
        getData: (d: typeof reportData) => d.heatmapData.cvss,
      },
      {
        value: 'aars',
        label: 'AARS — AI Agent Risk Score',
        title: 'AARS Risk Heatmap',
        subtitle: 'AI Agent Risk Score breakdown',
        visKey: 'heatmapAars' as const,
        getData: (d: typeof reportData) => d.heatmapData.aars,
      },
      {
        value: 'overall',
        label: 'Overall — Combined Assessment',
        title: 'Overall Risk Heatmap',
        subtitle: 'Combined AIVSS, CVSS, and AARS risk assessment',
        visKey: 'heatmapOverall' as const,
        getData: (d: typeof reportData) => [
          ...d.heatmapData.aivss,
          ...d.heatmapData.cvss,
          ...d.heatmapData.aars,
        ],
      },
      {
        value: 'vulnerability',
        label: 'Vulnerability Breakdown',
        title: 'Vulnerability Risk Heatmap',
        subtitle: 'Identified vulnerabilities mapped by likelihood and impact',
        visKey: 'heatmapVulnerability' as const,
        getData: (d: typeof reportData) => d.heatmapData.vulnerability,
      },
    ],
    []
  );
  const GROUPS_PER_PAGE = 10;
  const [vulnerabilityFilter, setVulnerabilityFilter] = useState<RiskLevel | null>(null);
  const [groupsPage, setGroupsPage] = useState(1);
  // Inline derived-state reset: when filter changes, jump back to page 1.
  const [prevFilter, setPrevFilter] = useState(vulnerabilityFilter);
  if (prevFilter !== vulnerabilityFilter) {
    setPrevFilter(vulnerabilityFilter);
    setGroupsPage(1);
  }
  const [activeHeatmap, setActiveHeatmap] = useState<string>('aivss');
  const [sectionVisibility, setSectionVisibility] = useState<SectionVisibility>({
    executiveSummary: true,
    keyFindings: true,
    pluginProfile: true,
    scoringBreakdown: true,
    creatorVerification: true,
    aiBom: false, // Now only accessible from menu
    vulnerabilityAssessment: true,
    threatModeling: true,
    permissionScope: true,
    prioritizedRemediation: true,
    riskAcceptanceMatrix: true,
    recommendation: true,
    rfiaSection: true,
    vibeguardSection: true,
    complianceCrosswalk: false, // Now only accessible from menu
    heatmapAivss: true,
    heatmapCvss: true,
    heatmapAars: true,
    heatmapOverall: true,
    heatmapVulnerability: true,
  });

  const filteredVulnerabilities = vulnerabilityFilter
    ? reportData.vulnerabilityDetails.filter((v) => v.severity === vulnerabilityFilter)
    : reportData.vulnerabilityDetails;

  const groupedVulnerabilities = useMemo(
    () => groupVulnerabilities(filteredVulnerabilities),
    [filteredVulnerabilities]
  );
  const totalGroupPages = Math.ceil(groupedVulnerabilities.length / GROUPS_PER_PAGE);
  const pagedGroups = groupedVulnerabilities.slice(
    (groupsPage - 1) * GROUPS_PER_PAGE,
    groupsPage * GROUPS_PER_PAGE
  );

  // Show loading state
  if (loading) {
    return <PageSpinner label={scanId ? `Fetching scan ${scanId}` : 'Preparing report data…'} />;
  }

  // Show failed scan view — just the error, not a full report
  if (scanStatus === 'failed') {
    return (
      <>
        <ReportPageShell>
          <div className="rounded-xl border border-zinc-700 bg-zinc-800/80 p-8">
            <div className="flex items-center gap-3 mb-6">
              <XCircle className="w-8 h-8 text-zinc-400" />
              <h1 className="text-xl font-semibold text-white">Scan Failed</h1>
            </div>

            <div className="space-y-4">
              {failureTarget && (
                <div>
                  <span className="text-sm text-zinc-400">Target</span>
                  <p className="text-white font-mono text-sm mt-1">{failureTarget}</p>
                </div>
              )}

              {scanId && (
                <div>
                  <span className="text-sm text-zinc-400">Scan ID</span>
                  <button
                    type="button"
                    onClick={() => copyScanId(scanId)}
                    className="mt-1 text-left text-white font-mono text-sm hover:text-green-400 transition-colors cursor-pointer"
                    aria-label="Copy scan ID"
                    title={scanIdCopied ? 'Copied!' : 'Click to copy'}
                  >
                    {scanId}
                  </button>
                  <p className="text-xs text-zinc-400 mt-1">
                    {scanIdCopied ? 'Copied!' : 'Click to copy'}
                  </p>
                </div>
              )}

              {failureDate && (
                <div>
                  <span className="text-sm text-zinc-400">Date</span>
                  <p className="text-zinc-300 text-sm mt-1 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {new Date(failureDate).toLocaleString()}
                  </p>
                </div>
              )}

              <div>
                <span className="text-sm text-zinc-400">Error</span>
                <div className="mt-2 rounded-lg bg-zinc-900 border border-zinc-700 p-4">
                  <p className="text-red-400 font-mono text-sm whitespace-pre-wrap">
                    {failureReason || 'Unknown error'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <UIButton
                variant="outline"
                className="gap-2"
                onClick={() => {
                  if (scanId) removeScan(scanId);
                  navigate('/reports');
                }}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Reports
              </UIButton>
              <UIButton
                className="bg-green-600 hover:bg-green-700 text-white gap-2"
                onClick={() => {
                  if (scanId) removeScan(scanId);
                  setRetryModalOpen(true);
                }}
              >
                <RotateCcw className="w-4 h-4" />
                Retry Scan
              </UIButton>
            </div>
          </div>
        </ReportPageShell>
        <ScanModal
          open={retryModalOpen}
          onOpenChange={setRetryModalOpen}
          defaultUrl={failureTarget}
        />
      </>
    );
  }

  // No real data available (no data source found, or API error with no data to show).
  // Never fall through to fake/sample report data — show an honest empty or error state.
  if (!loading && !reportDataResult) {
    return (
      <ReportPageShell>
        <div className="rounded-xl border border-zinc-700 bg-zinc-800/80 p-8">
          <div className="flex items-center gap-3 mb-6">
            {error ? (
              <AlertCircle className="w-8 h-8 text-amber-400" />
            ) : (
              <FileSearch className="w-8 h-8 text-zinc-400" />
            )}
            <h1 className="text-xl font-semibold text-white">
              {error ? 'Report Unavailable' : 'No Report Data Available'}
            </h1>
          </div>
          {error ? (
            <div className="mb-6">
              <p className="text-zinc-300 text-sm mb-3">
                The report could not be loaded. No scan data will be displayed.
              </p>
              <div className="rounded-lg bg-zinc-900 border border-zinc-700 p-4">
                <p className="text-red-400 font-mono text-sm whitespace-pre-wrap">{error}</p>
              </div>
            </div>
          ) : (
            <p className="text-zinc-300 text-sm mb-6">
              No scan data was found. To generate a report, run a scan and open the report link
              provided, or supply a <code className="text-green-400 font-mono">?scan_id=</code> or{' '}
              <code className="text-green-400 font-mono">?token=</code> URL parameter.
            </p>
          )}
          <UIButton variant="outline" className="gap-2" onClick={() => navigate('/reports')}>
            <ArrowLeft className="w-4 h-4" />
            Back to Reports
          </UIButton>
        </div>
      </ReportPageShell>
    );
  }

  const handlePrint = () => {
    // Store visibility state and open printable report
    sessionStorage.setItem('reportVisibility', JSON.stringify(sectionVisibility));
    window.open('/report/print' + window.location.search, '_blank');
  };

  const handleExportDownload = async (format: 'json' | 'markdown' | 'sarif') => {
    if (!scanId) return;
    setExportMenuOpen(false);

    const apiUrl = getApiUrl();
    const token = getStoredToken();

    const url = `${apiUrl}/v2/scan/${scanId}/${format}`;
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const response = await fetch(url, { headers });
      if (!response.ok) {
        console.error(`[Export] ${format} export failed: ${response.status}`);
        return;
      }
      const blob = await response.blob();
      const ext = format === 'markdown' ? 'md' : format === 'sarif' ? 'sarif' : 'json';
      const filename = `scan-${scanId}.${ext}`;
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error(`[Export] ${format} export error:`, err);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleScoreClick = (scoreType: 'aivss' | 'cvss' | 'aars' | 'creatorTrust') => {
    if (scoreType === 'creatorTrust') {
      scrollToSection('creator-verification');
    } else {
      scrollToSection('scoring-breakdown');
    }
  };

  const handleVulnerabilityCategoryClick = (category: RiskLevel) => {
    setVulnerabilityFilter(category);
    scrollToSection('vulnerability-assessment');
  };

  // Generate visible TOC items based on section visibility
  // Determine if RFIA data is available (controls section + TOC visibility)
  const hasRfiaData = !!(reportData.rfiaAggregate && reportData.rfiaAggregate.filesScanned > 0);
  // Determine if VGE data is available
  const hasVibeguardData = !!(reportData.vibeguardFindings && reportData.vibeguardFindings.length > 0);

  const tocItems = tocItemsConfig
    .filter((item) => {
      if (item.visibilityKey === 'heatmaps') {
        // Special case: show if any of the heatmaps are visible
        return (
          sectionVisibility.heatmapAivss ||
          sectionVisibility.heatmapCvss ||
          sectionVisibility.heatmapAars ||
          sectionVisibility.heatmapOverall
        );
      }
      // RFIA section only visible when scan included RFIA data
      if (item.visibilityKey === 'rfiaSection') {
        return hasRfiaData && sectionVisibility.rfiaSection;
      }
      // VGE section only visible when vibeguard findings exist
      if (item.visibilityKey === 'vibeguardSection') {
        return hasVibeguardData && sectionVisibility.vibeguardSection;
      }
      return sectionVisibility[item.visibilityKey as keyof SectionVisibility];
    })
    .map(({ id, label }) => ({ id, label }));

  return (
    <div className="min-h-screen bg-[rgb(5,5,5)]">
      {/* Traffic Light Accent Bar */}
      <div className="h-1 w-full bg-gradient-to-r from-red-600 via-yellow-500 to-green-500" />

      <ReportSubHeader
        title="AI Risk Assessment"
        subtitle={reportData.pluginName || 'Plugin & Agent Security Analysis'}
        actions={
          <>
            <ReportSettings
              visibility={sectionVisibility}
              onVisibilityChange={setSectionVisibility}
            />
            <UIButton onClick={handlePrint} className="gap-2">
              <Printer className="w-4 h-4" />
              Print / Save as PDF
            </UIButton>
            {isDynamic && scanId && (
              <div className="relative">
                <UIButton
                  variant="outline"
                  className="gap-2"
                  onClick={() => setExportMenuOpen((open) => !open)}
                >
                  <Download className="w-4 h-4" />
                  Export
                  <ChevronDown className="w-3 h-3" />
                </UIButton>
                {exportMenuOpen && (
                  <div className="absolute right-0 mt-1 w-44 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden">
                    <button
                      className="w-full px-4 py-2.5 text-sm text-left text-zinc-200 hover:bg-zinc-800 hover:text-emerald-400 transition-colors flex items-center gap-2"
                      onClick={() => handleExportDownload('json')}
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download JSON
                    </button>
                    <button
                      className="w-full px-4 py-2.5 text-sm text-left text-zinc-200 hover:bg-zinc-800 hover:text-emerald-400 transition-colors flex items-center gap-2"
                      onClick={() => handleExportDownload('markdown')}
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download Markdown
                    </button>
                    <button
                      className="w-full px-4 py-2.5 text-sm text-left text-zinc-200 hover:bg-zinc-800 hover:text-emerald-400 transition-colors flex items-center gap-2"
                      onClick={() => handleExportDownload('sarif')}
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download SARIF
                    </button>
                  </div>
                )}
              </div>
            )}
            <UIButton
              variant="outline"
              className="gap-2"
              onClick={() => navigate(isDynamic ? '/reports' : '/')}
            >
              <ArrowLeft className="w-4 h-4" />
              {isDynamic ? 'Back to Reports' : 'Back to Home'}
            </UIButton>
          </>
        }
      />

      <div className="container mx-auto px-6 py-8" ref={reportRef}>
        {/* Live scan indicator */}
        {isDynamic && (
          <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <span className="text-sm text-emerald-600">
              Showing live scan data{scanId ? ` (${scanId})` : ''}
            </span>
          </div>
        )}

        {/* GitHub Token Warning — show when token was explicitly absent or unknown */}
        {isDynamic && reportData.githubTokenPresent !== true && reportData.reportId && (
          <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-400">Limited scan — no GitHub token</h4>
              <p className="text-sm text-muted-foreground mt-1">
                This scan ran without a GitHub token. GitHub API rate limits (60 requests/hour) may
                have prevented full analysis of the repository, creator verification, and dependency
                scanning. Connect your GitHub account in Settings or configure a server-side token
                to enable complete results.
              </p>
            </div>
          </div>
        )}

        {/* Report Header */}
        <ReportHeader
          reportId={reportData.reportId}
          reportDate={reportData.reportDate}
          pluginName={reportData.pluginName}
          branch={reportData.branch}
          classification={reportData.classification}
        />

        {/* Executive Summary */}
        {sectionVisibility.executiveSummary && (
          <CollapsibleSection
            id="executive-summary"
            icon={BarChart3}
            title="Executive Summary"
            subtitle="Overall risk assessment and scoring metrics - Click any score for details"
            defaultOpen={true}
          >
            {/* Recommendation Card in Executive Summary */}
            <RecommendationCard
              type={reportData.recommendation.type}
              summary={reportData.recommendation.summary}
              overallScore={reportData.scores.overall}
            />

            <div className="grid md:grid-cols-4 gap-6 mt-6">
              <ScoreCard
                title="AIVSS Score"
                score={reportData.scores.aivss.score}
                riskLevel={reportData.scores.aivss.level}
                description="AI Vulnerability Scoring"
                clickable
                onClick={() => handleScoreClick('aivss')}
              />
              <ScoreCard
                title="CVSS Base Score"
                score={reportData.scores.cvss.score}
                riskLevel={reportData.scores.cvss.level}
                description="Common Vulnerability Score"
                clickable
                onClick={() => handleScoreClick('cvss')}
              />
              <ScoreCard
                title="AARS Score"
                score={reportData.scores.aars.score}
                maxScore={100}
                riskLevel={reportData.scores.aars.level}
                description="AI Agent Risk Score"
                clickable
                onClick={() => handleScoreClick('aars')}
              />
              {reportData.scores.creatorTrust && (
                <ScoreCard
                  title="Creator Trust™"
                  score={reportData.scores.creatorTrust.score}
                  riskLevel={reportData.scores.creatorTrust.level}
                  description="Verified creator score"
                  clickable
                  onClick={() => handleScoreClick('creatorTrust')}
                />
              )}
              {reportData.scores.crs && (
                <ScoreCard
                  title="SAFE™ Score"
                  score={reportData.scores.crs.score}
                  maxScore={1000}
                  riskLevel={reportData.scores.crs.level}
                  description="Composite Risk Score (higher is better)"
                />
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Table of Contents */}
        <div className="mb-8">
          <TableOfContents items={tocItems} />
        </div>

        {/* Key Findings */}
        {sectionVisibility.keyFindings && (
          <CollapsibleSection
            id="key-findings"
            icon={CheckCircle}
            title="Key Findings"
            subtitle="Critical discoveries from the security analysis - Click any finding for details"
            defaultOpen={true}
          >
            <div className="grid gap-3 mb-8">
              {reportData.keyFindings.length > 0 ? (
                reportData.keyFindings.map((finding, index) => (
                  <KeyFinding
                    key={index}
                    finding={finding.finding}
                    status={finding.status}
                    details={finding.details}
                    linkedSection={finding.linkedSection}
                  />
                ))
              ) : (
                <p className="text-muted-foreground text-sm text-center py-4">
                  No key findings reported for this scan.
                </p>
              )}
            </div>

            <VulnerabilitySummary
              counts={reportData.vulnerabilities}
              onCategoryClick={handleVulnerabilityCategoryClick}
            />
          </CollapsibleSection>
        )}

        {/* Plugin Profile - Moved up after Key Findings */}
        {sectionVisibility.pluginProfile && (
          <CollapsibleSection
            id="plugin-profile"
            icon={Settings}
            title="Plugin Profile"
            subtitle="Basic information about the analyzed plugin"
            defaultOpen={true}
          >
            <PluginProfile plugin={reportData.plugin} />
          </CollapsibleSection>
        )}

        {/* Creator Verification - Moved above Scoring Breakdown */}
        {sectionVisibility.creatorVerification && (
          <CollapsibleSection
            id="creator-verification"
            icon={Shield}
            title="VendorGuard (Creator Verification)"
            subtitle="Identity and trust assessment of the plugin creator"
            defaultOpen={true}
          >
            <CreatorVerification creator={reportData.creator} />
          </CollapsibleSection>
        )}

        {/* Scoring Breakdown */}
        {sectionVisibility.scoringBreakdown && (
          <CollapsibleSection
            id="scoring-breakdown"
            icon={TrendingUp}
            title="Scoring Breakdown"
            subtitle="Detailed analysis of each scoring metric"
            defaultOpen={true}
          >
            <div className="grid lg:grid-cols-3 gap-6">
              <ScoreBreakdown
                scoreType="AIVSS"
                totalScore={reportData.scores.aivss.score}
                riskLevel={reportData.scores.aivss.level}
                metrics={reportData.scoreBreakdowns.aivss}
              />
              <ScoreBreakdown
                scoreType="CVSS"
                totalScore={reportData.scores.cvss.score}
                riskLevel={reportData.scores.cvss.level}
                metrics={reportData.scoreBreakdowns.cvss}
              />
              <ScoreBreakdown
                scoreType="AARS"
                totalScore={reportData.scores.aars.score}
                maxScore={100}
                riskLevel={reportData.scores.aars.level}
                metrics={reportData.scoreBreakdowns.aars}
              />
            </div>
          </CollapsibleSection>
        )}

        {/* Unified Risk Heatmap with dropdown selector */}
        {(sectionVisibility.heatmapAivss ||
          sectionVisibility.heatmapCvss ||
          sectionVisibility.heatmapAars ||
          sectionVisibility.heatmapOverall ||
          sectionVisibility.heatmapVulnerability) &&
          (() => {
            const visibleOptions = heatmapOptions.filter((o) => sectionVisibility[o.visKey]);
            const resolved = visibleOptions.find((o) => o.value === activeHeatmap)
              ? activeHeatmap
              : (visibleOptions[0]?.value ?? 'aivss');
            const activeOption = heatmapOptions.find((o) => o.value === resolved);
            return (
              <CollapsibleSection
                id="risk-heatmaps"
                icon={BarChart3}
                title="Risk Assessment Heatmaps"
                subtitle="Visual risk analysis mapping likelihood vs impact"
                defaultOpen={true}
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-sm text-muted-foreground shrink-0">Scoring model:</span>
                  <Select value={resolved} onValueChange={setActiveHeatmap}>
                    <SelectTrigger className="w-72">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {visibleOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {activeOption && (
                  <RiskHeatmap
                    key={resolved}
                    title={activeOption.title}
                    subtitle={activeOption.subtitle}
                    dataPoints={activeOption.getData(reportData)}
                  />
                )}
              </CollapsibleSection>
            );
          })()}

        {/* Rules File Integrity Analysis (RFIA) — shown only when scan included RFIA */}
        {hasRfiaData && sectionVisibility.rfiaSection && reportData.rfiaAggregate && (
          <CollapsibleSection
            id="rfia"
            icon={Shield}
            title="Rules File Integrity Analysis"
            subtitle="Integrity analysis of AI coding assistant rules files (.cursorrules, CLAUDE.md, etc.)"
            defaultOpen={true}
          >
            <RFIASection
              findings={reportData.rfiaFindings || []}
              aggregate={reportData.rfiaAggregate}
            />
          </CollapsibleSection>
        )}

        {/* VibeGuard Engine */}
        {hasVibeguardData && reportData.vibeguardFindings && (
          <CollapsibleSection
            id="vibeguard"
            icon={Shield}
            title="VibeGuard Engine"
            subtitle="AI agent firewall — behavioral patterns across bash injection, credentials, network egress, and prompt injection"
            defaultOpen={true}
          >
            <VibeguardSection
              findings={reportData.vibeguardFindings}
            />
          </CollapsibleSection>
        )}

        {/* Vulnerability Assessment */}
        {sectionVisibility.vulnerabilityAssessment && (
          <CollapsibleSection
            id="vulnerability-assessment"
            icon={AlertTriangle}
            title="Vulnerability Assessment"
            subtitle={
              vulnerabilityFilter
                ? `Showing ${vulnerabilityFilter.toUpperCase()} severity vulnerabilities`
                : 'Detailed analysis of identified security issues'
            }
            defaultOpen={true}
          >
            {vulnerabilityFilter && (
              <div className="mb-4 flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Filtered by:{' '}
                  <span className="font-semibold capitalize">{vulnerabilityFilter}</span>
                </span>
                <UIButton variant="ghost" size="sm" onClick={() => setVulnerabilityFilter(null)}>
                  Clear filter
                </UIButton>
              </div>
            )}
            <div className="space-y-3">
              {pagedGroups.map((group) => (
                <VulnerabilityGroup
                  key={group.title}
                  title={group.title}
                  vulnerabilities={group.vulnerabilities}
                  repoTarget={reportData.plugin.location}
                />
              ))}
              {filteredVulnerabilities.length === 0 && (
                <p className="text-muted-foreground text-center py-8">
                  No vulnerabilities found in this category.
                </p>
              )}
              {totalGroupPages > 1 && (
                <Pagination className="mt-4">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#vulnerability-assessment"
                        onClick={(e) => {
                          e.preventDefault();
                          if (groupsPage > 1) setGroupsPage(groupsPage - 1);
                        }}
                        aria-disabled={groupsPage <= 1}
                        className={groupsPage <= 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    {Array.from({ length: totalGroupPages }, (_, i) => i + 1).map((p) => (
                      <PaginationItem key={p}>
                        <PaginationLink
                          href="#vulnerability-assessment"
                          isActive={p === groupsPage}
                          onClick={(e) => {
                            e.preventDefault();
                            setGroupsPage(p);
                          }}
                        >
                          {p}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem>
                      <PaginationNext
                        href="#vulnerability-assessment"
                        onClick={(e) => {
                          e.preventDefault();
                          if (groupsPage < totalGroupPages) setGroupsPage(groupsPage + 1);
                        }}
                        aria-disabled={groupsPage >= totalGroupPages}
                        className={
                          groupsPage >= totalGroupPages ? 'pointer-events-none opacity-50' : ''
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Threat Modeling - New Section */}
        {sectionVisibility.threatModeling && (
          <CollapsibleSection
            id="threat-modeling"
            icon={Target}
            title="Threat Modeling"
            subtitle="Analysis of potential threat actors, attack vectors, and critical assets"
            defaultOpen={true}
          >
            <ThreatModeling
              threatActors={reportData.threatModeling.threatActors}
              attackVectors={reportData.threatModeling.attackVectors}
              assetsCritical={reportData.threatModeling.assetsCritical ?? []}
            />
          </CollapsibleSection>
        )}

        {/* Permission Scope */}
        {sectionVisibility.permissionScope && (
          <CollapsibleSection
            id="permission-scope"
            icon={Lock}
            title="Permission Scope Analysis"
            subtitle="Capabilities and tools exposed by the plugin - Click tools for details"
            defaultOpen={true}
          >
            <MCPToolsSummary
              totalTools={reportData.mcpTools.totalTools}
              highRiskTools={reportData.mcpTools.highRiskTools}
              mediumRiskTools={reportData.mcpTools.mediumRiskTools}
              lowRiskTools={reportData.mcpTools.lowRiskTools}
              toolsList={reportData.mcpTools.toolsList}
              toolsInfo={reportData.mcpTools.toolsInfo}
              onRunOriginAnalysis={(_toolName) => {
                // Origin analysis placeholder — implementation pending
              }}
            />
            <PermissionScope permissions={reportData.permissions} toolDetails={toolDetailsData} />
          </CollapsibleSection>
        )}

        {/* Prioritized Remediation - New Section */}
        {sectionVisibility.prioritizedRemediation && (
          <CollapsibleSection
            id="prioritized-remediation"
            icon={Wrench}
            title="Prioritized Remediation Recommendations"
            subtitle="Actionable steps to address identified vulnerabilities ordered by priority"
            defaultOpen={true}
          >
            <PrioritizedRemediation items={reportData.remediationItems} />
          </CollapsibleSection>
        )}

        {/* Risk Acceptance Matrix - New Section */}
        {sectionVisibility.riskAcceptanceMatrix && (
          <CollapsibleSection
            id="risk-acceptance"
            icon={Scale}
            title="Risk Acceptance Matrix"
            subtitle="Documented risk decisions with ownership and review dates"
            defaultOpen={true}
          >
            <RiskAcceptanceMatrix items={reportData.riskAcceptanceItems} />
          </CollapsibleSection>
        )}

        {/* Final Recommendation */}
        {sectionVisibility.recommendation && (
          <CollapsibleSection
            id="recommendation"
            icon={FileSearch}
            title="Final Recommendation"
            subtitle="Approval status and required conditions"
            defaultOpen={true}
          >
            <RecommendationCard
              type={reportData.recommendation.type}
              summary={reportData.recommendation.summary}
              conditions={reportData.recommendation.conditions}
              overallScore={reportData.scores.overall}
            />
          </CollapsibleSection>
        )}

        {/* Footer */}
        <ReportFooter generatedDate={reportData.generatedAt} />
      </div>
    </div>
  );
};

export default Index;
